import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export type VideoType = 'youtube' | 'vimeo';

declare global {
  interface Window {
    klaro: any;
    klaroConfig: any;
  }
}

@customElement('imos-video-player')
export class ImosVideoPlayer extends LitElement {
  @property({ type: String, attribute: 'video-id' }) videoId = '';
  @property({ type: String, attribute: 'video-type' }) videoType: VideoType = 'vimeo';
  @property({ type: String, attribute: 'poster-image' }) posterImage = '';
  @property({ type: Boolean, attribute: 'activate-on-init' }) activateOnInit = false;
  @property({ type: String, attribute: 'embed-html' }) embedHtml = '';

  @state() private activated = false;
  @state() private needsConsent = false;
  @state() private awaitingConsent = false;
  @state() private oneTimeConsent = false;

  private boundProcessConsent = this.processConsent.bind(this);
  private boundStopVideo = this.handleStopVideo.bind(this);

  protected createRenderRoot(): this {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('videoconsent', this.boundProcessConsent as EventListener);
    window.addEventListener('stopvideo', this.boundStopVideo as EventListener);

    if (this.activateOnInit) {
      this.activate();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('videoconsent', this.boundProcessConsent as EventListener);
    window.removeEventListener('stopvideo', this.boundStopVideo as EventListener);
  }

  loadVideo(videoId: string, embedHtml?: string): void {
    this.videoId = videoId;
    this.embedHtml = embedHtml ?? '';
    this.activated = false;
    this.needsConsent = false;
    this.awaitingConsent = false;
    this.oneTimeConsent = false;
  }

  private get videoEmbedUrl(): string {
    if (!this.activated) return '';
    if (this.videoType === 'youtube') {
      return `https://www.youtube-nocookie.com/embed/${this.videoId}?autoplay=1`;
    }
    return `https://player.vimeo.com/video/${this.videoId}?autoplay=1`;
  }

  private addAutoplay(embedHtml: string): string {
    return embedHtml.replace(/src="([^"]+)"/, (_, src) => {
      const separator = src.includes('?') ? '&' : '?';
      return `src="${src}${separator}autoplay=1"`;
    });
  }

  private get posterImageUrl(): string {
    return this.posterImage || '';
  }

  private get hasConsent(): boolean {
    return (
      this.oneTimeConsent ||
      !window.klaro ||
      window.klaro.getManager().getConsent(this.videoType)
    );
  }

  private activate(): void {
    if (this.hasConsent) {
      this.activated = true;
    } else {
      this.needsConsent = true;
    }
  }

  deactivate(): void {
    this.activated = false;
  }

  private handleStopVideo(e: CustomEvent): void {
    if (e.detail === this.videoId) {
      this.activated = false;
    }
  }

  private processConsent(e: CustomEvent): void {
    if (this.awaitingConsent && e.detail[this.videoType]) {
      this.activate();
    }
  }

  private applyOneTimeConsent(): void {
    this.oneTimeConsent = true;
    this.activate();
  }

  private applyConsent(): void {
    const klaroCookieExists = document.cookie.split(';').some((item) => {
      return item.trim().indexOf(window.klaro.getManager().storageName + '=') === 0;
    });

    if (!klaroCookieExists) {
      window.klaro.show(undefined, true);
    }

    window.klaro.getManager().updateConsent(this.videoType, true);

    if (klaroCookieExists) {
      window.klaro.getManager().saveAndApplyConsents();
      this.activate();
    } else {
      this.awaitingConsent = true;
    }
  }

  private cancelConsent(): void {
    this.activated = false;
    if (!this.activateOnInit) {
      this.needsConsent = false;
    }
    this.dispatchEvent(
      new CustomEvent('imos-video-cancel-consent', {
        bubbles: true,
        detail: this.videoId,
      }),
    );
  }

  // Consent notice HTML comes from klaroConfig which is a trusted, developer-controlled config
  private get consentNotice(): string {
    const lang = document.documentElement.lang;
    return window.klaroConfig?.translations?.[lang]?.[this.videoType]?.onVideoNotice ?? '';
  }

  private get singleConsentButtonText(): string {
    const lang = document.documentElement.lang;
    return window.klaroConfig?.translations?.[lang]?.[this.videoType]?.onVideoSingleConsent ?? '';
  }

  private get permanentConsentButtonText(): string {
    const lang = document.documentElement.lang;
    return window.klaroConfig?.translations?.[lang]?.[this.videoType]?.onVideoPermanentConsent ?? '';
  }

  private get layerClasses(): string {
    return this.needsConsent
      ? 'fixed bg-gray-300/80 lg:absolute'
      : 'absolute';
  }

  protected render() {
    if (this.activated) {
      if (this.embedHtml) {
        return html`
          <div class="aspect-video [&>iframe]:w-full [&>iframe]:h-full">
            ${unsafeHTML(this.addAutoplay(this.embedHtml))}
          </div>
        `;
      }
      return html`
        <div class="aspect-video">
          <iframe
            class="w-full h-full"
            src=${this.videoEmbedUrl}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowfullscreen
          ></iframe>
        </div>
      `;
    }

    return html`
      <div class="aspect-video">
        <div class="w-full h-full flex justify-center items-center">
          <div class="relative h-full w-full">
            ${this.posterImageUrl
              ? html`<img
                  src=${this.posterImageUrl}
                  alt=""
                  loading="lazy"
                  width="1280"
                  height="720"
                  class="h-full w-full object-cover"
                />`
              : nothing}
            <div
              class="inset-0 flex justify-center items-center bg-black/80 transition-colors ${this.layerClasses}"
            >
              ${this.needsConsent
                ? html`
                    <div
                      class="bg-gray-400/90 lg:bg-none p-xs text-sm w-4/5 xl:w-3/5"
                    >
                      <!-- consentNotice is from trusted klaroConfig, not user input -->
                      <div class="text-white">
                      <p>${unsafeHTML(this.consentNotice)}</p>
                      </div>
                      <div
                        class="flex flex-wrap sm:flex-nowrap justify-center items-center gap-x-xs gap-y-3xs"
                      >
                        <button
                          class="btn btn--xs btn--primary sm:btn--sm"
                          @click=${() => this.applyOneTimeConsent()}
                        >
                          ${this.singleConsentButtonText}
                        </button>
                        <button
                          class="btn btn--xs btn--primary sm:btn--sm"
                          @click=${() => this.applyConsent()}
                        >
                          ${this.permanentConsentButtonText}
                        </button>
                        <div class="w-full sm:hidden"></div>
                        <button
                          class="btn btn--xs btn--primary sm:btn--sm btn--link"
                          @click=${() => this.cancelConsent()}
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  `
                : html`
                    <button
                      @click=${() => this.activate()}
                      class="rounded-full bg-brand w-xl h-xl text-white stretched-link"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path
                          fill="currentColor"
                          d="M401.65,110.35a206,206,0,1,0-291.3,291.3,206,206,0,1,0,291.3-291.3ZM256,440C154.52,440,72,357.48,72,256S154.52,72,256,72s184,82.56,184,184S357.48,440,256,440Z"
                        />
                        <path
                          fill="currentColor"
                          d="M371.85,254.27,190,147.88a2,2,0,0,0-3,1.72V362.4a2,2,0,0,0,3,1.72l181.9-106.39A2,2,0,0,0,371.85,254.27ZM206.94,331V181l128.25,75Z"
                        />
                      </svg>
                      <span class="sr-only">Play</span>
                    </button>
                  `}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
